import { useState, useMemo } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useHolidays } from '@/hooks/useHolidays';
import type { Deadline, Holiday } from '@/types/deadlines';
import { getDaysUntil, getUrgencyLevel } from '@/services/dateCalculationService';

interface DeadlineCalendarProps {
  deadlines: Deadline[];
  onDateClick?: (date: Date, deadlines: Deadline[]) => void;
  onDeadlineClick?: (deadline: Deadline) => void;
  selectedDate?: Date;
}

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

type UrgencyLevel = 'vencido' | 'hoy' | 'urgente' | 'proximo' | 'normal' | 'completado';

export function DeadlineCalendar({ 
  deadlines, 
  onDateClick,
  onDeadlineClick,
  selectedDate 
}: DeadlineCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const { getHolidaysForMonth, autonomousCommunity } = useHolidays();
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Obtener festivos del mes
  const monthHolidays = useMemo(() => {
    return getHolidaysForMonth(year, month);
  }, [year, month, getHolidaysForMonth]);

  // Crear mapa de festivos por fecha
  const holidaysByDate = useMemo(() => {
    const map = new Map<string, Holiday>();
    monthHolidays.forEach(holiday => {
      map.set(holiday.date, holiday);
    });
    return map;
  }, [monthHolidays]);

  // Crear mapa de plazos por fecha
  const deadlinesByDate = useMemo(() => {
    const map = new Map<string, Deadline[]>();
    
    deadlines.forEach(deadline => {
      const dateKey = deadline.due_date;
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)!.push(deadline);
    });
    
    return map;
  }, [deadlines]);

  // Generar días del mes incluyendo días del mes anterior y siguiente
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Ajustar para que la semana empiece en lunes (0 = lunes, 6 = domingo)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    
    // Días del mes anterior para completar la primera semana
    const prevMonthLastDay = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Días del mes actual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }
    
    // Días del mes siguiente para completar la última semana (hasta tener 6 filas = 42 días)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [year, month]);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return selectedDate?.toDateString() === date.toDateString();
  };

  const getHighestUrgency = (dateDeadlines: Deadline[]): UrgencyLevel | null => {
    if (!dateDeadlines.length) return null;
    
    const urgencyPriority: UrgencyLevel[] = ['vencido', 'hoy', 'urgente', 'proximo', 'normal', 'completado'];
    let highestUrgency: UrgencyLevel = 'completado';
    
    for (const deadline of dateDeadlines) {
      const daysUntil = getDaysUntil(new Date(deadline.due_date));
      const urgency = getUrgencyLevel(daysUntil, deadline.status) as UrgencyLevel;
      
      if (urgencyPriority.indexOf(urgency) < urgencyPriority.indexOf(highestUrgency)) {
        highestUrgency = urgency;
      }
    }
    
    return highestUrgency;
  };

  const getUrgencyBadgeClass = (urgency: UrgencyLevel | null): string => {
    if (!urgency) return '';
    
    const classes: Record<UrgencyLevel, string> = {
      vencido: 'bg-deadline-expired',
      hoy: 'bg-deadline-today',
      urgente: 'bg-deadline-urgent',
      proximo: 'bg-deadline-soon',
      normal: 'bg-deadline-normal',
      completado: 'bg-deadline-completed',
    };
    
    return classes[urgency];
  };

  return (
    <div className="space-y-4">
      {/* Header con navegación */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h3 className="text-lg font-semibold ml-2">
            {MONTHS[month]} {year}
          </h3>
        </div>
        
        <Button variant="outline" size="sm" onClick={goToToday}>
          <CalendarIcon className="h-4 w-4 mr-2" />
          Hoy
        </Button>
      </div>

      {/* Leyenda */}
      <div className="flex items-center gap-4 text-xs flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-deadline-expired" />
          <span className="text-muted-foreground">Vence hoy</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-deadline-urgent" />
          <span className="text-muted-foreground">1-3 días</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-deadline-soon" />
          <span className="text-muted-foreground">4-7 días</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-deadline-normal" />
          <span className="text-muted-foreground">+7 días</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-deadline-completed" />
          <span className="text-muted-foreground">Completado</span>
        </div>
        <div className="flex items-center gap-1.5 border-l pl-4">
          <Star className="h-3 w-3 text-holiday fill-holiday" />
          <span className="text-muted-foreground">Festivo</span>
        </div>
      </div>

      {/* Calendario */}
      <div className="border rounded-lg overflow-hidden">
        {/* Encabezados de días */}
        <div className="grid grid-cols-7 bg-muted/50">
          {WEEKDAYS.map(day => (
            <div 
              key={day} 
              className="py-2 text-center text-sm font-medium text-muted-foreground"
            >
              {day}
            </div>
          ))}
        </div>
        
        {/* Días del mes */}
        <div className="grid grid-cols-7">
          <TooltipProvider>
          {calendarDays.map((dayData, index) => {
            const { date, isCurrentMonth } = dayData;
            const dateKey = getDateKey(date);
            const dateDeadlines = deadlinesByDate.get(dateKey) || [];
            const urgency = getHighestUrgency(dateDeadlines);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const holiday = holidaysByDate.get(dateKey);
            
            return (
              <div
                key={`${dateKey}-${index}`}
                className={cn(
                  'min-h-24 border-t border-r last:border-r-0 p-1.5 cursor-pointer transition-colors overflow-hidden',
                  !isCurrentMonth && 'bg-muted/30 opacity-50',
                  isCurrentMonth && isWeekend && 'bg-muted/10',
                  isCurrentMonth && holiday && 'bg-holiday/10',
                  isCurrentMonth && !isWeekend && !holiday && 'hover:bg-muted/30',
                  isToday(date) && 'bg-primary/5 ring-1 ring-primary/20 ring-inset',
                  isSelected(date) && 'bg-primary/10 ring-2 ring-primary ring-inset'
                )}
                onClick={() => isCurrentMonth && onDateClick?.(date, dateDeadlines)}
              >
                <div className="flex items-start justify-between">
                  <span className={cn(
                    'text-sm font-medium w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0',
                    isToday(date) && 'bg-primary text-primary-foreground',
                    !isCurrentMonth && 'text-muted-foreground',
                    isCurrentMonth && (isWeekend || holiday) && !isToday(date) && 'text-muted-foreground'
                  )}>
                    {date.getDate()}
                  </span>
                  
                  {isCurrentMonth && dateDeadlines.length > 0 && (
                    <div className={cn(
                      'w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm',
                      getUrgencyBadgeClass(urgency)
                    )}>
                      {dateDeadlines.length}
                    </div>
                  )}
                </div>
                
                {/* Indicador de festivo - mostrado debajo del número */}
                {isCurrentMonth && holiday && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="mt-1 flex items-start gap-1 bg-holiday/20 rounded px-1.5 py-1 border border-holiday/40 cursor-help">
                        <Star className="h-3 w-3 text-holiday fill-holiday flex-shrink-0 mt-0.5" />
                        <span className="text-[10px] text-holiday font-semibold leading-tight">
                          {holiday.name}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="font-semibold">{holiday.name}</p>
                      <p className="text-xs text-muted-foreground">
                        ({holiday.scope === 'nacional' ? 'Nacional' : 
                          holiday.scope === 'andalucia' ? 'Autonómico - Andalucía' :
                          holiday.scope === 'extremadura' ? 'Autonómico - Extremadura' : 
                          'Festividad Local'})
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
                
                {/* Mostrar hasta 2 plazos del día */}
                {isCurrentMonth && dateDeadlines.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {dateDeadlines.slice(0, 2).map((deadline) => (
                      <button
                        key={deadline.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeadlineClick?.(deadline);
                        }}
                        className={cn(
                          'w-full text-left text-[10px] leading-tight truncate px-1 py-0.5 rounded transition-colors',
                          deadline.status === 'completado' 
                            ? 'bg-deadline-completed/10 text-muted-foreground line-through hover:bg-deadline-completed/20' 
                            : 'bg-muted/50 hover:bg-muted'
                        )}
                        title={`Clic para editar: ${deadline.title}`}
                      >
                        {deadline.title}
                      </button>
                    ))}
                    {dateDeadlines.length > 2 && (
                      <p className="text-[10px] text-muted-foreground px-1">
                        +{dateDeadlines.length - 2} más
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
